/**
 * iOS Heartbeat Service
 *
 * Manages periodic heartbeat signals to the native iOS app via WebKit message handlers.
 * This service helps detect and prevent app freezes/crashes by maintaining active
 * communication with the native layer.
 *
 * @module IOSHeartbeatService
 */

/**
 * Configuration for the heartbeat mechanism
 */
const HEARTBEAT_CONFIG = {
  INTERVAL_MS: 5000, // 5 seconds
  MESSAGE_NAME: 'heartbeat',
  HANDLER_NAME: 'vizzleNative', // The webkit message handler name
  MAX_RETRIES: 3,
  RETRY_DELAY_MS: 1000,
};

/**
 * State management for the heartbeat service
 */
const heartbeatState = {
  isRunning: false,
  intervalId: null,
  lastHeartbeatTime: null,
  heartbeatCount: 0,
  failureCount: 0,
  isIOSEnvironment: false,
};

/**
 * Detect if the app is running in an iOS environment
 * @returns {boolean} True if running in iOS WebView
 */
const isIOSEnvironment = () => {
  if (heartbeatState.isIOSEnvironment !== null && heartbeatState.isIOSEnvironment !== undefined) {
    return heartbeatState.isIOSEnvironment;
  }

  const userAgent = navigator.userAgent || '';
  const isWebKit = !!(window.webkit && window.webkit.messageHandlers);
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);

  heartbeatState.isIOSEnvironment = isWebKit && isIOS;
  return heartbeatState.isIOSEnvironment;
};

/**
 * Stop the heartbeat mechanism
 * @returns {void}
 */
const stopHeartbeat = () => {
  if (!heartbeatState.isRunning) {
    return;
  }

  if (heartbeatState.intervalId) {
    clearInterval(heartbeatState.intervalId);
    heartbeatState.intervalId = null;
  }

  heartbeatState.isRunning = false;
  // eslint-disable-next-line no-console
  console.info('[IOSHeartbeat] Stopped. Total heartbeats sent:', heartbeatState.heartbeatCount);
};

/**
 * Send a heartbeat message to the native iOS app
 * @returns {Promise<void>}
 */
const sendHeartbeat = async () => {
  try {
    if (!isIOSEnvironment()) {
      return;
    }

    const heartbeatMessage = {
      type: HEARTBEAT_CONFIG.MESSAGE_NAME,
      timestamp: Date.now(),
      heartbeatCount: heartbeatState.heartbeatCount,
      appState: 'active',
    };

    // Send message to native iOS handler
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.vizzleNative) {
      window.webkit.messageHandlers.vizzleNative.postMessage(heartbeatMessage);
      heartbeatState.lastHeartbeatTime = Date.now();
      heartbeatState.heartbeatCount += 1;
      heartbeatState.failureCount = 0; // Reset failure count on success

      // eslint-disable-next-line no-console
      console.debug('[IOSHeartbeat] Heartbeat sent', {
        count: heartbeatState.heartbeatCount,
        timestamp: heartbeatMessage.timestamp,
      });
    } else {
      // eslint-disable-next-line no-console
      console.warn('[IOSHeartbeat] Native handler not available');
      heartbeatState.failureCount += 1;
    }
  } catch (error) {
    heartbeatState.failureCount += 1;
    // eslint-disable-next-line no-console
    console.error('[IOSHeartbeat] Error sending heartbeat:', error);

    // If too many failures, stop the heartbeat to avoid continuous errors
    if (heartbeatState.failureCount >= HEARTBEAT_CONFIG.MAX_RETRIES) {
      // eslint-disable-next-line no-console
      console.warn('[IOSHeartbeat] Max retries reached, stopping heartbeat');
      stopHeartbeat();
    }
  }
};

/**
 * Start the heartbeat mechanism
 * @param {number} [intervalMs=HEARTBEAT_CONFIG.INTERVAL_MS] Custom interval in milliseconds
 * @returns {void}
 */
const startHeartbeat = (intervalMs = HEARTBEAT_CONFIG.INTERVAL_MS) => {
  if (heartbeatState.isRunning) {
    // eslint-disable-next-line no-console
    console.debug('[IOSHeartbeat] Heartbeat already running');
    return;
  }

  if (!isIOSEnvironment()) {
    // eslint-disable-next-line no-console
    console.debug('[IOSHeartbeat] Not in iOS environment, skipping heartbeat');
    return;
  }

  // Send initial heartbeat immediately
  sendHeartbeat();

  // Set up recurring heartbeat
  heartbeatState.intervalId = setInterval(() => {
    sendHeartbeat();
  }, intervalMs);

  heartbeatState.isRunning = true;

  // eslint-disable-next-line no-console
  console.info('[IOSHeartbeat] Started with interval:', intervalMs, 'ms');
};

/**
 * Get current heartbeat status
 * @returns {Object} Current state of the heartbeat mechanism
 */
const getHeartbeatStatus = () => ({
  isRunning: heartbeatState.isRunning,
  isIOSEnvironment: isIOSEnvironment(),
  heartbeatCount: heartbeatState.heartbeatCount,
  failureCount: heartbeatState.failureCount,
  lastHeartbeatTime: heartbeatState.lastHeartbeatTime,
  intervalMs: HEARTBEAT_CONFIG.INTERVAL_MS,
});

/**
 * Reset heartbeat statistics
 * @returns {void}
 */
const resetHeartbeatStats = () => {
  heartbeatState.heartbeatCount = 0;
  heartbeatState.failureCount = 0;
  heartbeatState.lastHeartbeatTime = null;
  // eslint-disable-next-line no-console
  console.debug('[IOSHeartbeat] Statistics reset');
};

/**
 * Configure or change the heartbeat interval
 * @param {number} intervalMs New interval in milliseconds
 * @returns {void}
 */
const setHeartbeatInterval = (intervalMs) => {
  if (intervalMs <= 0) {
    // eslint-disable-next-line no-console
    console.error('[IOSHeartbeat] Invalid interval:', intervalMs);
    return;
  }

  HEARTBEAT_CONFIG.INTERVAL_MS = intervalMs;

  // Restart with new interval if already running
  if (heartbeatState.isRunning) {
    stopHeartbeat();
    startHeartbeat(intervalMs);
  }

  // eslint-disable-next-line no-console
  console.info('[IOSHeartbeat] Interval updated to:', intervalMs, 'ms');
};

/**
 * Public API for the iOS Heartbeat Service
 */
const IOSHeartbeatService = {
  startHeartbeat,
  stopHeartbeat,
  sendHeartbeat,
  getHeartbeatStatus,
  resetHeartbeatStats,
  setHeartbeatInterval,
  isIOSEnvironment,
};

export default IOSHeartbeatService;
